import { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  ArrowRight,
  ArrowUpRight,
  Download,
  Mail,
  Calendar,
  Headphones,
  BookOpen,
  Clock,
  Check,
  Loader2,
} from "lucide-react";

const GUIDE_URL = "/products/the-5-minute-creative-reactivation-formula.pdf";
const COACHING_CALENDLY = "https://calendly.com/artofyou/begincreative";
const ART_THERAPY_CALENDLY = "https://calendly.com/artofyou/art-therapy-consultation";
const SOULSOUND_URL = "https://soulsound-alchemy.com";
const EMAIL = "contact@maria-katharina-richters.com";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Welcome() {
  const location = useLocation();
  const firstName = location.state?.firstName?.trim();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const paidParam = query.get("paid");
  const sessionId = query.get("session_id");

  const [paidStatus, setPaidStatus] = useState(null); // null | 'pending' | 'paid' | 'expired' | 'error'
  const [downloadUrl, setDownloadUrl] = useState(null);

  useEffect(() => {
    document.title = "Welcome · Awakened Artist Academy";
    // Per-page SEO meta
    const desc = "You're in. Your free Creative Reset is on its way. Step gently into the rest of the Academy.";
    const setMeta = (sel, attr, val) => {
      let el = document.head.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', desc);
    setMeta('meta[property="og:title"]', 'content', 'Welcome · Awakened Artist Academy');
    setMeta('meta[property="og:description"]', 'content', desc);
    setMeta('meta[name="twitter:description"]', 'content', desc);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Poll payment status when arriving from Stripe success redirect.
  useEffect(() => {
    if (paidParam !== "companion" || !sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const max = 8;
    setPaidStatus("pending");

    const tick = async () => {
      if (cancelled) return;
      if (attempts >= max) {
        setPaidStatus((s) => (s === "paid" ? s : "error"));
        return;
      }
      attempts += 1;
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        const ps = res.data?.payment_status;
        if (ps === "paid") {
          setPaidStatus("paid");
          setDownloadUrl(res.data?.download_url || null);
          return;
        }
        if (res.data?.status === "expired") {
          setPaidStatus("expired");
          return;
        }
        setTimeout(tick, 1800);
      } catch (e) {
        setTimeout(tick, 2000);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [paidParam, sessionId]);

  const showCompanionCard = paidStatus !== "paid";

  return (
    <div data-testid="welcome-page" className="min-h-screen bg-paper text-[#1A1A1A] flex flex-col">
      <Navbar />

      <main className="flex-1">
        {paidParam === "companion" && (
          <PaidCompanionBanner status={paidStatus} downloadUrl={downloadUrl} />
        )}
        <WelcomeHero firstName={firstName} />
        {showCompanionCard && <CompanionCard />}
        <WhileYouRead />
        <Pathways />
        <TheBecoming />
        <StayClose />
        <DeeperStory />
        <BookingCTA />
      </main>

      <Footer />
    </div>
  );
}

function PaidCompanionBanner({ status, downloadUrl }) {
  const isPaid = status === "paid";
  const isPending = status === "pending" || status === null;
  return (
    <section
      data-testid="paid-companion-banner"
      className="bg-[#1FA5B8] text-[#F7F5F2] border-b border-[#1FA5B8]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-5 md:py-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <div className="flex items-center gap-3 flex-1">
          {isPending ? (
            <Loader2 size={18} strokeWidth={1.6} className="animate-spin" />
          ) : isPaid ? (
            <span className="h-7 w-7 inline-flex items-center justify-center bg-[#F7F5F2] text-[#1FA5B8]">
              <Check size={14} strokeWidth={2.4} />
            </span>
          ) : null}
          <div>
            <div className="font-display italic text-[18px] md:text-[20px] leading-tight">
              {isPending && "Confirming your purchase…"}
              {isPaid && "Thank you. The Deeper Reactivation is yours."}
              {status === "expired" && "That checkout expired."}
              {status === "error" && "We are still confirming your payment."}
            </div>
            <div className="font-body text-[12px] uppercase tracking-[0.22em] text-[#F7F5F2]/80 mt-1">
              {isPending && "One moment…"}
              {isPaid && "A copy is also on its way to your inbox"}
              {(status === "expired" || status === "error") && "Please refresh or check your email"}
            </div>
          </div>
        </div>
        {isPaid && downloadUrl && (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="paid-download-link"
            className="inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-black text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-11 px-5 transition-colors"
          >
            <Download size={14} strokeWidth={1.6} />
            Download the PDF
          </a>
        )}
      </div>
    </section>
  );
}

function CompanionCard() {
  return (
    <section
      data-testid="welcome-companion-card"
      className="bg-[#F7F5F2] border-b border-[#E5E0D8]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-10 md:py-12">
        <div className="border border-[#E5E0D8] bg-[#FFFFFF] p-6 md:p-8 grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 md:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body">
              A quiet companion piece
            </div>
            <h3 className="mt-3 font-display text-[#1A1A1A] text-[22px] md:text-[26px] leading-[1.25] tracking-tight">
              If today felt like a doorway, there is a small{" "}
              <span className="italic font-light">next room</span>.
            </h3>
            <p className="mt-3 font-body text-[#5C5C5C] text-sm md:text-base leading-relaxed max-w-2xl">
              The Deeper Reactivation is a short, optional PDF that picks up
              where the free guide ends. Five dollars. No pressure, no
              subscription. Only here if it is the right day for it.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end">
            <Link
              to="/the-deeper-reactivation"
              data-testid="welcome-companion-cta"
              className="inline-flex items-center justify-center gap-2 border border-[#1A1A1A] hover:border-[#1FA5B8] hover:text-[#1FA5B8] text-[#1A1A1A] font-body text-[12px] uppercase tracking-[0.22em] h-11 px-5 transition-colors"
            >
              See the Deeper Reactivation
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function WelcomeHero({ firstName }) {
  return (
    <section
      data-testid="welcome-hero"
      className="relative overflow-hidden border-b border-[#E5E0D8]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16 md:pb-24">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#5C5C5C] hover:text-[#1FA5B8] font-body transition-colors"
        >
          <ArrowRight size={14} strokeWidth={1.5} className="rotate-180" />
          Back to the Academy
        </Link>

        <div className="mt-10 grid grid-cols-12 gap-8 items-start">
          <div className="col-span-12 md:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#1A1A1A]" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#5C5C5C] font-body">
                You&apos;re in. Your guide is on its way.
              </span>
            </div>

            <h1
              data-testid="welcome-headline"
              className="font-display text-[#1A1A1A] tracking-tighter leading-[0.98] text-[clamp(2.75rem,6.5vw,5.5rem)] font-medium"
            >
              Welcome{firstName ? <>, <span className="italic font-light">{firstName}</span></> : null}.
            </h1>

            <p className="mt-6 font-display italic text-[#1FA5B8] text-[clamp(1.4rem,2.6vw,2rem)] leading-tight max-w-2xl">
              Thank you for choosing yourself today.
            </p>

            <div className="mt-8 max-w-2xl font-body text-[#1A1A1A] text-lg leading-[1.75] space-y-5">
              <p>
                The 5-Minute Creative Reset is heading to your inbox now.
                Save it, print it, keep it close. There&apos;s no perfect
                time to start. Five minutes, whenever the next quiet one
                arrives.
              </p>
              <p className="text-[#5C5C5C]">
                While you wait, I wanted to share a little more of what lives
                here. The Awakened Artist Academy is one part of a wider
                practice. If anything below speaks to you, you&apos;re warmly
                welcome to explore.
              </p>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-center">
              <a
                href={GUIDE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="welcome-download-button"
                className="inline-flex items-center justify-center gap-3 bg-[#1FA5B8] hover:bg-[#178391] text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-12 px-6 transition-colors"
              >
                <Download size={16} strokeWidth={1.6} />
                Open the guide now
              </a>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="welcome-email-button"
                className="inline-flex items-center justify-center gap-3 border border-[#1A1A1A] hover:border-[#1FA5B8] hover:text-[#1FA5B8] text-[#1A1A1A] font-body text-[12px] uppercase tracking-[0.22em] h-12 px-6 transition-colors"
              >
                <Mail size={16} strokeWidth={1.6} />
                Say hello
              </a>
            </div>
          </div>

          {/* Side card */}
          <aside
            className="col-span-12 md:col-span-3 md:col-start-10 mt-12 md:mt-2 border border-[#E5E0D8] bg-[#FFFFFF] p-7 self-start"
            data-testid="welcome-side-card"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body">
              A small note
            </div>
            <div className="mt-4 font-display text-[#1A1A1A] text-[22px] leading-[1.3] tracking-tight">
              Reply to any email from the Academy.
            </div>
            <p className="mt-3 font-body text-sm text-[#5C5C5C] leading-relaxed">
              I read every one, slowly, with care. This is a quiet practice,
              not a marketing funnel.
            </p>
            <div className="mt-6 hairline" />
            <div className="mt-4 font-body text-[11px] uppercase tracking-[0.28em] text-[#5C5C5C]">
              Maria-Katharina
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

const pathways = [
  {
    k: "01",
    eyebrow: "Coaching",
    title: "The Becoming",
    body: "A 3-month container for the work that single sessions can&rsquo;t hold. Coaching, sound, and art therapy woven together. Voice notes between sessions when you need them. A clear before and after. For those ready to take real accountability for the transformation they want. I hold the space; you do the becoming.",
    cta: "Book a free 15-min Begin Again call",
    href: COACHING_CALENDLY,
    meta: [
      "Free 15-min Begin Again call",
      "Single 1:1 sessions · €120",
      "The Becoming · 3 months · from €1800",
    ],
  },
  {
    k: "02",
    eyebrow: "Art Therapy",
    title: "When words aren't enough",
    body: "Person-Oriented and Solution-Oriented art therapy for emotional overload, burnout, grief, life transitions, addictive patterns, and the things the rational mind hasn&rsquo;t yet found language for. For when you know you need more time and a gentler structure to walk through what has been difficult. You don&apos;t need to be an artist. You only need to show up.",
    cta: "Book an art therapy consultation",
    href: ART_THERAPY_CALENDLY,
    meta: [
      "Jávea, online, or home visits",
      "Single sessions · €70",
      "Starter pack · 3 sessions for €200",
    ],
  },
  {
    k: "03",
    eyebrow: "Sound Healing",
    title: "SoulSound Alchemy",
    body: "Sound is not something you hear, it&rsquo;s something you feel. Through crystal and Himalayan bowls, chimes and voice, the nervous system softens and the body remembers its natural balance. Weekly group journeys, private 1:1 sessions, home and group experiences, the signature Return Journey, and a few unusual gems like DRIFT (floating sound healing) and INTERSPACE (live sonic journeys).",
    cta: "Visit SoulSound Alchemy",
    href: SOULSOUND_URL,
    meta: [
      "Weekly group sessions · from €20",
      "Private 1:1 · from €120",
      "Spain & internationally, online too",
    ],
  },
];

function WhileYouRead() {
  const [ep, setEp] = useState(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/podcast/latest`)
      .then((res) => {
        if (active && res.data?.available) setEp(res.data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  if (!ep) return null;

  return (
    <section
      data-testid="while-you-read"
      className="bg-[#F7F5F2] border-b border-[#E5E0D8]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
              While you read the guide
            </div>
            <h2 className="mt-4 font-display text-[#1A1A1A] leading-[1.05] tracking-tight text-[clamp(1.75rem,3.4vw,2.75rem)] font-medium">
              Something to put in your{" "}
              <span className="italic font-light">ears</span>.
            </h2>
            <p className="mt-4 font-body text-[#5C5C5C] leading-relaxed max-w-md">
              The latest from Born to Create, my podcast for artists and
              aspiring creatives. A small companion for the same journey
              you&apos;re on.
            </p>
          </div>

          <article
            data-testid="while-you-read-episode"
            className="col-span-12 md:col-span-7 border border-[#E5E0D8] bg-[#FFFFFF] p-5 md:p-6"
          >
            <div className="grid grid-cols-12 gap-5 md:gap-6 items-center">
              <div className="col-span-4 md:col-span-3">
                <div className="border border-[#E5E0D8] bg-[#0A0A0A] aspect-square overflow-hidden">
                  {ep.image ? (
                    <img
                      src={ep.image}
                      alt={ep.short_title || "Latest episode"}
                      className="w-full h-full object-cover block"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#1FA5B8]">
                      <Headphones size={28} strokeWidth={1.4} />
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-8 md:col-span-9">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body">
                    {ep.episode_number ? `Episode ${ep.episode_number}` : "Latest episode"}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.28em] text-[#5C5C5C] font-body">
                    Born to Create
                  </span>
                </div>
                <h3 className="mt-3 font-display text-[#1A1A1A] text-[20px] md:text-[24px] leading-[1.2] tracking-tight">
                  {ep.short_title}
                </h3>
                <div className="mt-5 flex flex-wrap gap-3">
                  {ep.link && (
                    <a
                      href={ep.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="while-you-read-listen"
                      className="inline-flex items-center gap-2 bg-[#1FA5B8] hover:bg-[#178391] text-[#F7F5F2] font-body text-[11px] uppercase tracking-[0.22em] h-10 px-4 transition-colors"
                    >
                      <Headphones size={13} strokeWidth={1.6} />
                      Listen now
                    </a>
                  )}
                  {ep.description_html && (
                    <Sheet>
                      <SheetTrigger asChild>
                        <button
                          type="button"
                          data-testid="while-you-read-readalong"
                          className="inline-flex items-center gap-2 border border-[#1A1A1A] hover:border-[#1FA5B8] hover:text-[#1FA5B8] text-[#1A1A1A] font-body text-[11px] uppercase tracking-[0.22em] h-10 px-4 transition-colors"
                        >
                          <BookOpen size={13} strokeWidth={1.6} />
                          Read along
                        </button>
                      </SheetTrigger>
                      <SheetContent
                        side="right"
                        data-testid="readalong-sheet"
                        className="w-full sm:max-w-xl md:max-w-2xl bg-[#F7F5F2] border-l border-[#E5E0D8] p-0 overflow-y-auto"
                      >
                        <ReadAlongPanel ep={ep} />
                      </SheetContent>
                    </Sheet>
                  )}
                  <Link
                    to="/born-to-create"
                    data-testid="while-you-read-podcast-page"
                    className="inline-flex items-center gap-2 border border-[#E5E0D8] hover:border-[#1FA5B8] hover:text-[#1FA5B8] text-[#5C5C5C] font-body text-[11px] uppercase tracking-[0.22em] h-10 px-4 transition-colors"
                  >
                    All episodes
                    <ArrowUpRight size={13} strokeWidth={1.6} />
                  </Link>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

function ReadAlongPanel({ ep }) {
  return (
    <div data-testid="readalong-panel" className="h-full flex flex-col">
      {/* Cover header */}
      <div className="relative bg-[#0A0A0A] aspect-[16/9] w-full overflow-hidden">
        {ep.image && (
          <img
            src={ep.image}
            alt={ep.short_title || "Episode artwork"}
            className="w-full h-full object-cover opacity-70"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-[#F7F5F2]">
          <div className="flex items-center gap-4 flex-wrap text-[10px] uppercase tracking-[0.28em] font-body text-[#F7F5F2]/75">
            <span>Born to Create</span>
            {ep.episode_number && <span>· Episode {ep.episode_number}</span>}
            {ep.duration && (
              <span className="inline-flex items-center gap-1">
                <Clock size={11} strokeWidth={1.6} />
                {ep.duration}
              </span>
            )}
          </div>
          <h3 className="mt-3 font-display text-[26px] md:text-[32px] leading-[1.15] tracking-tight">
            {ep.short_title}
          </h3>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 px-6 md:px-10 py-8 md:py-10">
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body mb-4">
          Read along · Show notes
        </div>

        <div
          data-testid="readalong-body"
          className="readalong-prose font-body text-[#1A1A1A] text-[16px] leading-[1.85] space-y-4"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(ep.description_html, {
              ALLOWED_TAGS: ["p", "strong", "em", "br", "a", "ul", "ol", "li", "h2", "h3", "span"],
              ALLOWED_ATTR: ["href", "target", "rel"],
            }),
          }}
        />

        {ep.link && (
          <div className="mt-10 pt-8 border-t border-[#E5E0D8]">
            <p className="font-display italic text-[#5C5C5C] text-base mb-4">
              When you&apos;re ready, put it in your ears.
            </p>
            <a
              href={ep.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="readalong-listen"
              className="inline-flex items-center gap-2 bg-[#1FA5B8] hover:bg-[#178391] text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-11 px-5 transition-colors"
            >
              <Headphones size={14} strokeWidth={1.6} />
              Listen on Spotify
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function Pathways() {
  return (
    <section
      data-testid="pathways"
      className="bg-[#FFFFFF] border-b border-[#E5E0D8]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-8 items-end mb-16">
          <div className="col-span-12 md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
              Three pathways
            </div>
            <h2 className="mt-4 font-display text-[#1A1A1A] leading-[1.02] tracking-tight text-[clamp(2.25rem,5vw,4rem)] font-medium">
              Ways we can{" "}
              <span className="italic font-light">work</span> together.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <p className="font-body text-[#5C5C5C] text-base md:text-lg leading-relaxed">
              The Academy is the gentle doorway. These are the deeper rooms.
              Each one stands alone, and they often work beautifully together.
            </p>
          </div>
        </div>

        <ol className="grid grid-cols-12 gap-x-8 gap-y-12">
          {pathways.map((p, i) => (
            <li
              key={p.k}
              data-testid={`pathway-${p.k}`}
              className={`col-span-12 ${i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : "md:col-span-12"}`}
            >
              <article
                className={`group h-full border border-[#E5E0D8] bg-[#F7F5F2] p-8 md:p-10 flex flex-col ${
                  i === 2 ? "md:flex-row md:items-start md:gap-12" : ""
                }`}
              >
                <div className={`${i === 2 ? "md:w-1/2" : ""}`}>
                  <div className="flex items-baseline justify-between">
                    <span className="font-display text-[36px] leading-none tracking-tight text-[#1A1A1A]">
                      {p.k}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body">
                      {p.eyebrow}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-[#1A1A1A] text-[28px] md:text-[32px] leading-tight tracking-tight">
                    <span className="italic font-light">{p.title}</span>
                  </h3>
                </div>
                <div className={`${i === 2 ? "md:w-1/2 md:pt-0 mt-6 md:mt-0" : "mt-6"}`}>
                  <p
                    className="font-body text-[#1A1A1A] text-base leading-[1.7]"
                    dangerouslySetInnerHTML={{ __html: p.body }}
                  />

                  <ul className="mt-6 space-y-2 font-body text-[12px] uppercase tracking-[0.22em] text-[#5C5C5C]">
                    {p.meta.map((m, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="h-1 w-1 rounded-full bg-[#1FA5B8]" />
                        {m}
                      </li>
                    ))}
                  </ul>

                  <a
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`pathway-cta-${p.k}`}
                    className="mt-8 inline-flex items-center gap-2 font-body text-[12px] uppercase tracking-[0.22em] text-[#1A1A1A] group-hover:text-[#1FA5B8] transition-colors"
                  >
                    {p.cta}
                    <ArrowUpRight size={16} strokeWidth={1.6} />
                  </a>
                </div>
              </article>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function TheBecoming() {
  const points = [
    {
      n: "01",
      title: "You hold the work between sessions.",
      body: "Single sessions end at the door. Container clients have someone thinking about them across the weeks, noticing the patterns, holding the thread. They feel it, even when they can&rsquo;t name it.",
    },
    {
      n: "02",
      title: "Voice notes when you need them.",
      body: "Not endless support, just the right kind. Between sessions, you can reach for me when something stirs. Knowing the line is open changes how you show up to your own life.",
    },
    {
      n: "03",
      title: "Three modalities, not one.",
      body: "Coaching, sound healing, and art therapy, woven into a single container. We use what serves the moment. A coaching question one week, a sound session the next, an art therapy exercise when words aren&rsquo;t enough.",
    },
    {
      n: "04",
      title: "Accountability across time.",
      body: "One session creates insight. Twelve weeks create a new self. Real change asks for steady showing up, and the container is the only way to hold that with you.",
    },
    {
      n: "05",
      title: "A clear before and after.",
      body: "We rarely change in the middle of nothing. The Becoming gives you a definite starting line, a steady middle, and a closing ritual. A graduation that matters more than people expect.",
    },
    {
      n: "06",
      title: "You&rsquo;re saying yes to commitment.",
      body: "When you commit three months, something shifts inside you the moment you book. The container is the choice. The work that follows is the unfolding of that choice.",
    },
  ];

  return (
    <section
      data-testid="the-becoming"
      className="bg-[#1A1A1A] text-[#F7F5F2] border-b border-[#E5E0D8] relative overflow-hidden"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-8 items-end mb-16">
          <div className="col-span-12 md:col-span-8">
            <div className="flex items-center gap-4 flex-wrap">
              <h2 className="font-display leading-none tracking-tighter text-[clamp(3.75rem,9vw,7rem)] font-medium">
                The <span className="italic font-light">Becoming</span>
              </h2>
              <span className="border border-[#1FA5B8] text-[#1FA5B8] font-body text-[11px] uppercase tracking-[0.28em] px-3 py-1.5 leading-none">
                Signature Programme
              </span>
            </div>
            <p className="mt-6 font-display italic text-[#F7F5F2]/85 text-[clamp(1.4rem,2.4vw,2rem)] leading-tight">
              Not a stack of sessions. A held thread.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className="font-body text-[#F7F5F2]/70 text-base md:text-lg leading-relaxed">
              The Becoming is three months of someone holding the thread of
              your transformation with you. Between our calls, voice notes
              when you need them. Across the weeks, the woven work of
              coaching, sound, and art therapy. At the end, a different self.
            </p>
          </div>
        </div>

        <ul className="grid grid-cols-12 gap-x-8 gap-y-12">
          {points.map((p) => (
            <li
              key={p.n}
              data-testid={`becoming-point-${p.n}`}
              className="col-span-12 md:col-span-6 lg:col-span-4 border-t border-[#F7F5F2]/30 pt-6"
            >
              <div className="flex items-baseline justify-between">
                <span className="font-display text-[26px] leading-none tracking-tight text-[#1FA5B8]">
                  {p.n}
                </span>
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#F7F5F2]/50 font-body">
                  Why it matters
                </span>
              </div>
              <h3
                className="mt-5 font-display text-[#F7F5F2] text-[22px] md:text-[24px] leading-[1.25] tracking-tight"
                dangerouslySetInnerHTML={{ __html: p.title }}
              />
              <p
                className="mt-3 font-body text-[#F7F5F2]/70 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: p.body }}
              />
            </li>
          ))}
        </ul>

        {/* Pricing block */}
        <div className="mt-20 border-t border-[#F7F5F2]/20 pt-12">
          <div className="grid grid-cols-12 gap-8 items-start">
            <div className="col-span-12 md:col-span-4">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
                Investment
              </div>
              <h3 className="mt-4 font-display text-[#F7F5F2] leading-tight tracking-tight text-[clamp(1.8rem,3.5vw,2.5rem)] font-medium">
                Three months,{" "}
                <span className="italic font-light">three</span> ways.
              </h3>
              <p className="mt-5 font-body text-[#F7F5F2]/60 text-sm leading-relaxed">
                Paying upfront is the simplest. Instalments are there if you
                need them, with a small uplift to keep the longer commitment
                fair on both sides.
              </p>
            </div>

            <ul className="col-span-12 md:col-span-7 md:col-start-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  tag: "Upfront",
                  amount: "\u20AC1800",
                  detail: "Paid at the start of the container",
                  highlight: true,
                },
                {
                  tag: "In two",
                  amount: "2 \u00D7 \u20AC975",
                  detail: "Half at start, half at midpoint",
                },
                {
                  tag: "Monthly",
                  amount: "3 \u00D7 \u20AC700",
                  detail: "Across the three months",
                },
              ].map((opt, i) => (
                <li
                  key={i}
                  data-testid={`becoming-price-${i + 1}`}
                  className={`border p-6 flex flex-col ${
                    opt.highlight
                      ? "border-[#1FA5B8] bg-[#1FA5B8]/10"
                      : "border-[#F7F5F2]/20"
                  }`}
                >
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body">
                    {opt.tag}
                  </div>
                  <div className="mt-3 font-display text-[#F7F5F2] text-[32px] leading-none tracking-tight">
                    {opt.amount}
                  </div>
                  <p className="mt-3 font-body text-[#F7F5F2]/60 text-xs leading-relaxed">
                    {opt.detail}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-center">
            <a
              href={COACHING_CALENDLY}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="becoming-cta"
              className="inline-flex items-center justify-center gap-3 bg-[#1FA5B8] hover:bg-[#178391] text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-12 px-6 transition-colors"
            >
              <Calendar size={16} strokeWidth={1.6} />
              Book a free 15-min Begin Again call
            </a>
            <p className="font-body text-[12px] text-[#F7F5F2]/50">
              No pressure. We&rsquo;ll see if this is the right fit, together.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function StayClose() {
  const companions = [
    {
      tag: "App",
      pretitle: "Self-coaching, art therapy, and quiet support in your pocket",
      title: "WYNN",
      rune: "ᚹ",
      body: "A pocket companion for self-coaching, instant pain release, and understanding your feelings more clearly. Inside, nervous system regulation through art and meditation, a journal, a quiet community, and one AI tool in my voice for the moments you want to reflect on your own. The name comes from Wunjo (ᚹ), the ancient rune of joy. The joy that arises when challenges resolve and we find ourselves in the right place, with the right people, in the right work.",
      features: [
        "Instant pain release tools",
        "Art therapy videos for everyday feelings",
        "Meditations for nervous system regulation",
        "Journal and quiet community space",
        "One AI tool for self-coaching, in my voice",
      ],
      price: "Free forever tier · Premium €12.99/month after a 30-day trial",
      cta: "Coming soon · Get notified",
      href: "#",
      status: "Coming soon",
    },
    {
      tag: "Podcast",
      pretitle: "Conversations with creatives who chose their art",
      title: "Born to Create",
      body: "A podcast for artists and aspiring creatives who want to unleash their full potential and dare to build a life around their work. Honest conversations with musicians, painters, photographers, and makers about creativity, freedom, and following the call. Hosted by me.",
      features: [
        "58 episodes and counting",
        "Artist interviews on creativity, freedom, and following the call",
        "Listen on Spotify, Apple Podcasts, or Amazon Music",
      ],
      price: "Free, always",
      cta: "Listen now",
      href: "https://born-to-create.onpodium.com/",
      status: "Live · 58 episodes",
    },
  ];

  return (
    <section
      id="stay-close"
      data-testid="stay-close"
      className="bg-[#FFFFFF] border-b border-[#E5E0D8]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-8 items-end mb-16">
          <div className="col-span-12 md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
              Daily companions
            </div>
            <h2 className="mt-4 font-display text-[#1A1A1A] leading-[1.02] tracking-tight text-[clamp(2.25rem,4.6vw,3.75rem)] font-medium">
              Ways to stay{" "}
              <span className="italic font-light">close</span> to the practice.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <p className="font-body text-[#5C5C5C] text-base md:text-lg leading-relaxed">
              Free, ambient, in-the-pocket support for the days between
              sessions. Or simply to keep the work quietly alive.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {companions.map((c, i) => (
            <article
              key={i}
              data-testid={`companion-${c.tag.toLowerCase()}`}
              className="col-span-12 md:col-span-6 border border-[#E5E0D8] bg-[#F7F5F2] p-8 md:p-10 flex flex-col relative overflow-hidden"
            >
              {c.rune && (
                <div
                  aria-hidden="true"
                  className="absolute -right-6 -bottom-10 select-none pointer-events-none font-display text-[#1FA5B8]/10 leading-none"
                  style={{ fontSize: "260px" }}
                >
                  {c.rune}
                </div>
              )}

              <div className="relative flex items-baseline justify-between">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body">
                  {c.tag}
                </span>
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#5C5C5C] font-body">
                  {c.status}
                </span>
              </div>

              <div className="relative mt-6">
                <p className="font-display italic text-[#5C5C5C] text-base leading-snug">
                  {c.pretitle}
                </p>
                <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                  <h3 className="font-display text-[#1A1A1A] text-[40px] md:text-[44px] leading-none tracking-tight">
                    {c.title}
                  </h3>
                  {c.rune && (
                    <span
                      aria-hidden="true"
                      className="font-display text-[#1FA5B8] text-[32px] md:text-[36px] leading-none"
                    >
                      {c.rune}
                    </span>
                  )}
                </div>
              </div>

              <p className="relative mt-6 font-body text-[#1A1A1A] text-base leading-[1.7]">
                {c.body}
              </p>

              <ul className="relative mt-6 space-y-2 font-body text-[13px] text-[#5C5C5C]">
                {c.features.map((f, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="h-1 w-1 rounded-full bg-[#1FA5B8] mt-2 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="relative mt-6 hairline" />
              <p className="relative mt-4 font-body text-[12px] uppercase tracking-[0.22em] text-[#5C5C5C]">
                {c.price}
              </p>

              <a
                href={c.href}
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                data-testid={`companion-cta-${c.tag.toLowerCase()}`}
                className="relative mt-6 inline-flex items-center gap-2 font-body text-[12px] uppercase tracking-[0.22em] text-[#1A1A1A] hover:text-[#1FA5B8] transition-colors w-fit"
              >
                {c.cta}
                <ArrowRight size={14} strokeWidth={1.6} />
              </a>
            </article>
          ))}
        </div>

        {/* Newsletter expectation note */}
        <div className="mt-16 max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body mb-3">
            About the letters
          </div>
          <p className="font-display italic text-[#1A1A1A] text-[clamp(1.2rem,1.8vw,1.5rem)] leading-[1.5]">
            You will hear from me roughly twice a month. Quiet letters from
            the studio. Honest, slow, never noisy. You can reply to any one.
          </p>
        </div>
      </div>
    </section>
  );
}

function DeeperStory() {
  return (
    <section
      data-testid="welcome-story"
      className="bg-[#F7F5F2] border-b border-[#E5E0D8]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="col-span-12 md:col-span-5">
            <div className="border border-[#E5E0D8]">
              <img
                src="https://customer-assets.emergentagent.com/job_five-min-formula/artifacts/wojkimz1_SP147%20copy.jpg"
                alt="Maria-Katharina Richters"
                className="w-full h-[560px] object-cover"
              />
            </div>
            <p className="mt-4 font-body text-[11px] uppercase tracking-[0.28em] text-[#5C5C5C]">
              Maria-Katharina Richters · Coach, Sound Healer, Art Therapist
            </p>
          </div>

          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body">
              A little more about me
            </div>
            <h2 className="mt-4 font-display text-[#1A1A1A] leading-[1.05] tracking-tight text-[clamp(2.25rem,4.4vw,3.5rem)] font-medium">
              What helps us{" "}
              <span className="italic font-light">reconnect</span> with
              ourselves?
            </h2>

            <div className="mt-8 space-y-5 font-body text-[#1A1A1A] text-[17px] leading-[1.75] max-w-xl">
              <p>
                That single question has guided my work for years. My own
                journey took me through art therapy, coaching, creativity,
                consciousness studies, sound healing and personal
                transformation.
              </p>
              <p>
                Along the way I discovered that so many of the struggles we
                experience, the stress, burnout, self-doubt, creative blocks,
                emotional pain and quiet sense of disconnection, often arise
                when we lose touch with who we truly are.
              </p>
              <p>
                The work I offer today brings everything I have learned
                through these disciplines into one practice. Not to fix
                people. Not to tell them who they should become. But to
                create spaces where they can reconnect with who they already
                are. Spaces where healing, insight, creativity and
                transformation can emerge naturally.
              </p>
              <p className="font-display italic text-[#1FA5B8] text-[clamp(1.2rem,1.8vw,1.5rem)] leading-[1.4] pt-2">
                Because beneath all the noise, your authentic self is already
                there. Waiting to be remembered.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-xl">
              <Stat n="Coach" label="Coaching" />
              <Stat n="Healer" label="Sound" />
              <Stat n="Therapist" label="Art" />
              <Stat n="Artist" label="Nature" />
            </div>

            <div className="mt-10 pt-8 border-t border-[#E5E0D8]">
              <Link
                to="/work-with-me"
                data-testid="welcome-to-wwm"
                className="inline-flex items-center gap-2 border border-[#1A1A1A] hover:border-[#1FA5B8] hover:text-[#1FA5B8] text-[#1A1A1A] font-body text-[12px] uppercase tracking-[0.22em] h-11 px-5 transition-colors"
              >
                Find out more about how I work
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ n, label }) {
  return (
    <div className="border-t border-[#1A1A1A] pt-4">
      <div className="font-display text-[26px] leading-none tracking-tight text-[#1A1A1A]">
        {n}
      </div>
      <div className="mt-2 font-body text-[11px] uppercase tracking-[0.22em] text-[#5C5C5C]">
        {label}
      </div>
    </div>
  );
}

function BookingCTA() {
  return (
    <section
      data-testid="welcome-cta"
      className="bg-[#1A1A1A] text-[#F7F5F2] relative overflow-hidden"
    >
      <div aria-hidden="true" className="absolute top-12 right-12 opacity-25 hidden md:block">
        <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
          <circle cx="60" cy="60" r="40" stroke="#F7F5F2" strokeWidth="0.6" strokeDasharray="1.4 4" />
          <circle cx="60" cy="60" r="22" stroke="#F7F5F2" strokeWidth="0.5" />
          <circle cx="60" cy="60" r="2" fill="#1FA5B8" />
          {[...Array(12)].map((_, i) => {
            const a = (i * 30 * Math.PI) / 180;
            return (
              <line
                key={i}
                x1={60 + Math.cos(a) * 50}
                y1={60 + Math.sin(a) * 50}
                x2={60 + Math.cos(a) * 56}
                y2={60 + Math.sin(a) * 56}
                stroke="#F7F5F2"
                strokeWidth="0.6"
              />
            );
          })}
        </svg>
      </div>

      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32 relative">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#F7F5F2]/60 font-body">
              When you&apos;re ready
            </div>
            <h2 className="mt-4 font-display leading-[0.98] tracking-tight text-[clamp(2.5rem,5vw,4.5rem)] font-medium">
              The next step is the{" "}
              <span className="italic font-light">smallest</span> one.
            </h2>
            <p className="mt-6 font-body text-[#F7F5F2]/70 text-lg leading-relaxed max-w-xl">
              A free 15-minute exploratory call, no pressure either way. We
              check in with each other about where you are and what you&apos;re
              looking for. If it feels right, we go deeper from there. A
              45-minute consultation, a single session, or a longer container.
              Whatever serves the moment you&apos;re in.
            </p>
          </div>

          <div className="col-span-12 md:col-span-4 flex flex-col gap-3">
            <a
              href={COACHING_CALENDLY}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-book-call"
              className="inline-flex items-center justify-center gap-3 bg-[#1FA5B8] hover:bg-[#178391] text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-14 px-6 transition-colors"
            >
              <Calendar size={18} strokeWidth={1.6} />
              Book a free 15-min call
            </a>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="cta-email"
              className="inline-flex items-center justify-center gap-3 border border-[#F7F5F2]/40 hover:border-[#1FA5B8] hover:text-[#1FA5B8] text-[#F7F5F2] font-body text-[12px] uppercase tracking-[0.22em] h-14 px-6 transition-colors"
            >
              <Mail size={18} strokeWidth={1.6} />
              Or write to me
            </a>
            <p className="mt-2 font-body text-xs text-[#F7F5F2]/50 text-center">
              {EMAIL}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
